import { use, useState } from "react";
import { useDbClient, useLiveQuery } from "@tanstack/react-db";
import {
  AccountCredits as CreditsView,
  type AccountReadState,
  type AccountCreditsData,
} from "@softmax/product-ux/account";
import {
  QueryViewerContext,
  collectionError,
  collectionRefreshing,
  refreshCollections,
} from "@softmax/web-features/data";
import { ApiError } from "@softmax/observatory-client/transport";
import { myCreditsCollection } from "@/features/usage/records";
import { buildCreditsView } from "./credits-format";

export function AccountCredits() {
  const viewerId = use(QueryViewerContext);
  const dbClient = useDbClient();
  const descriptor = myCreditsCollection({
    viewerId,
    elevatedPrivileges: false,
  });
  const query = useLiveQuery((q) => q.from({ credits: descriptor }).findOne());
  const error = collectionError(query.health);
  const unavailable =
    error instanceof ApiError && (error.status === 401 || error.status === 403);
  const [refreshing, setRefreshing] = useState(false);
  const refresh = async () => {
    setRefreshing(true);

    try {
      await refreshCollections({
        signal: dbClient.requireDependency<AbortController>("lifetime").signal,
        queries: [query.collection],
        refresh: () =>
          dbClient.collection(descriptor).utils.refetch({ throwOnError: true }),
      });
    } finally {
      setRefreshing(false);
    }
  };
  let state: AccountReadState<AccountCreditsData> = { status: "loading" };

  if (query.data && !unavailable) {
    state = {
      status: "ready",
      data: buildCreditsView(query.data),
    };

    if (error)
      state = {
        ...state,
        refresh: { status: "error", message: error.message },
      };
    else if (refreshing || collectionRefreshing(query.health))
      state = { ...state, refresh: { status: "refreshing" } };
  } else if (error) state = { status: "error", message: error.message };

  return (
    <CreditsView
      state={state}
      onRetry={() => {
        if (!refreshing) void refresh();
      }}
    />
  );
}
